/**
 * Native course Help on the Desk (#1294 R3, docs/contracts/coursework.md#native-course-help).
 *
 * A learner's one durable thread with the course's current active assigned
 * teaching team, and that team's list of learner threads, unanswered first.
 * The server (RLS and the message guard) decides every read and write; this
 * draws them. Sends use actor-owned receipts: a send whose answer is unknown,
 * including after a reload, offers Check on its same key and never sends again.
 * An authority refusal (401, 403, 404) ends a read at once: its cached private
 * bodies go and nothing acts on them. Only a transient failure keeps a stale read.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useQueries, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { Link, useLocation, useSearchParams } from "react-router";
import { useAuth } from "@/contexts/AuthContext";
import { Bento } from "@/components/ui/box";
import { useTabDrafts, type TabDraft } from "@/components/ui/box-draft";
import type { Blocker, Box, FactBox, ListBox, Read, Result, RowTask, TaskBox } from "@/components/ui/box.schema";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api/client";
import { reasonOnError } from "@/lib/api/commitSeams";
import { courseHelpApi, courseHelpKey, type HelpMessage, type HelpReference, type HelpSent, type HelpThread, type HelpThreadRow, type HelpThreads, type MyHelp } from "@/lib/api/courseHelp";
import { formatCourseInstant } from "@/lib/seat-workspace";
import { helpWords as words } from "./help-copy";
import { formatRetentionDate, helpHref, readHelpArrival, type HelpArrival } from "./help-address";

/** A contextual entry: a quiet link from a session or a piece of work to the learner's Help. */
export function HelpEntryLink({ courseHref, reference }: { courseHref: string; reference: { kind: "session" | "work"; id: string; label?: string | null } }) {
  return <Link data-help-entry={reference.kind} className="inline-flex min-h-11 items-center text-ui font-medium underline underline-offset-4"
    to={helpHref(courseHref, reference)} state={{ helpLabel: reference.label ?? null }}>
    {reference.kind === "session" ? words.askAboutSession : words.askAboutWork}
  </Link>;
}

interface QuestionDraft { body: string; reference: HelpReference | null }
interface PendingSend { key: string; body: string }
type Decision = { state: "confirmed"; sent: HelpSent } | { state: "refused"; sentence: string } | { state: "unknown" };

const EMPTY: ReadonlySet<string> = new Set();
const isRefusal = (error: unknown) => error instanceof ApiError && [401, 403, 404].includes(error.status);

/**
 * Reads this identity was refused. A refused read is not asked again for the
 * same tenant, login and course, so removing its cache cannot loop; another
 * identity starts clean.
 */
function useRefusals(identity: string) {
  const [held, setHeld] = useState<{ identity: string; reads: ReadonlySet<string> }>({ identity, reads: EMPTY });
  const refused = held.identity === identity ? held.reads : EMPTY;
  const refuse = useCallback((read: string) => setHeld(was => {
    const reads = was.identity === identity ? was.reads : EMPTY;
    return reads.has(read) ? was : { identity, reads: new Set(reads).add(read) };
  }), [identity]);
  return { refused, refuse };
}

/** Runs a read and records an authority refusal before React Query keeps its error. */
function refusing<T>(read: string, refuse: (read: string) => void, run: () => Promise<T>) {
  return async () => {
    try { return await run(); } catch (error) { if (isRefusal(error)) refuse(read); throw error; }
  };
}

function readOf<T>(query: UseQueryResult<T>, failed: string, empty?: (value: T) => string | null): Read<T> {
  if (query.data !== undefined) {
    if (query.isRefetchError) return { status: "stale", last: query.data, readAt: new Date(query.dataUpdatedAt).toISOString(), sentence: failed, refresh: () => void query.refetch() };
    const sentence = empty?.(query.data);
    return sentence ? { status: "empty", sentence } : { status: "ready", value: query.data, readAt: new Date(query.dataUpdatedAt).toISOString() };
  }
  return query.isError ? { status: "failed", sentence: failed, retry: () => void query.refetch() } : { status: "loading" };
}

function author(message: HelpMessage) {
  if (message.author.is_me) return words.you;
  return message.author.name ?? (message.author.kind === "teacher" ? words.teacher : words.learner);
}

const sameReference = (a: HelpReference | null, b: HelpReference | null) => a?.kind === b?.kind && a?.id === b?.id;

/**
 * The thread in order. Each message names its author; a learner's says whether it is answered.
 * `readThrough` is the saved read mark that showing the thread moves; `newAfter` (default the
 * same) marks replies New, so a reply read now stays New while it is on screen.
 */
export function HelpMessages({ messages, timezone, readThrough, newAfter = readThrough, onSeen }: { messages: HelpMessage[]; timezone: string | null; readThrough?: number; newAfter?: number; onSeen?: (seq: number) => void }) {
  const last = messages.at(-1)?.seq ?? 0;
  useEffect(() => { if (onSeen && readThrough !== undefined && last > readThrough) onSeen(last); }, [onSeen, readThrough, last]);
  return <ol className="grid gap-3" aria-label={words.thread}>
    {messages.map(message => <li key={message.id} data-help-message={message.author.kind} className="grid gap-1 border-s-2 border-rule ps-3">
      <p className="text-meta text-muted-foreground">
        <span className="font-medium text-foreground">{author(message)}</span>
        {" · "}{formatCourseInstant(message.created_at, timezone) ?? ""}
        {message.author.kind === "learner" && <>{" · "}{message.answered ? words.answered : words.open}</>}
        {newAfter !== undefined && message.author.kind === "teacher" && message.seq > newAfter && <>{" · "}<span className="font-medium text-foreground">{words.newSince}</span></>}
      </p>
      {message.reference && <p className="text-meta text-muted-foreground">{words.aboutReference(message.reference.label)}</p>}
      <p dir="auto" className="whitespace-pre-wrap">{message.body}</p>
    </li>)}
  </ol>;
}

/** One send's lifecycle under a receipt key, shared by the learner's question and a teacher's reply. */
function useSend<D>({ scope, draft, send, check, done, notSent, invalidate }: {
  scope: string; draft: TabDraft<D, PendingSend>;
  send(key: string, body: string): Promise<HelpSent>; check(key: string): ReturnType<typeof courseHelpApi.check>;
  done: string; notSent: string; invalidate(): void;
}) {
  const [results, setResults] = useState<Record<string, Result>>({});
  const result = results[scope] ?? { state: "none" };
  const setResult = (next: Result) => setResults(was => ({ ...was, [scope]: next }));
  const inFlight = useRef(new Map<string, Promise<Result>>());
  const sending = useRef(new Set<string>());
  function exclusive(run: () => Promise<Result>): Promise<Result> {
    const running = inFlight.current.get(scope);
    if (running) return running;
    const request = Promise.resolve().then(run).finally(() => { inFlight.current.delete(scope); });
    inFlight.current.set(scope, request);
    return request;
  }
  const unknown = (pending: PendingSend): Result => ({ state: "unknown", cap: words.notSent, requestId: pending.key, sentence: words.sendUnknown,
    check: { verb: words.checkStatus, note: words.checkOnly, run: () => exclusive(async () => {
      try {
        const answer = await check(pending.key);
        if (answer.state === "not_committed") return settle({ state: "refused", sentence: words.notSentCheck });
        if (!("receipt" in answer)) return settle({ state: "unknown" }, pending);
        return settle(answer.receipt.result.state === "confirmed" ? { state: "confirmed", sent: answer.receipt.result } : { state: "unknown" }, pending);
      } catch { return settle({ state: "unknown" }, pending); }
    }) } });
  function settle(decision: Decision, pending?: PendingSend): Result {
    const next: Result = decision.state === "unknown" && pending ? unknown(pending)
      : decision.state === "confirmed" ? { state: "confirmed", at: new Date().toISOString(), effects: [done], remains: [] }
        : { state: "refused", cap: notSent, sentence: decision.state === "refused" ? decision.sentence : words.sendUnknown, draft: "kept" };
    if (next.state !== "unknown") draft.setPending(null);
    if (decision.state === "confirmed") { draft.discard(); invalidate(); }
    setResult(next);
    return next;
  }
  const drawn: Result = result.state === "none" && draft.pending && !sending.current.has(scope) ? unknown(draft.pending) : result;
  const run = (key: string, body: string, blockers: Blocker[]) => exclusive(async () => {
    if (draft.pending) return unknown(draft.pending);
    const blocked = blockers[0];
    if (blocked && "sentence" in blocked) return settle({ state: "refused", sentence: blocked.sentence });
    const pending = { key, body };
    sending.current.add(scope);
    draft.setPending(pending);
    try { return settle({ state: "confirmed", sent: await send(key, body) }, pending); }
    catch (error) {
      return settle(error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 408
        ? { state: "refused", sentence: reasonOnError(error)?.message ?? error.message } : { state: "unknown" }, pending);
    } finally { sending.current.delete(scope); }
  });
  return {
    result: drawn, run, frozen: Boolean(draft.pending),
    /** An edit starts again from any answer but an unknown one, which only Check settles. */
    clear: () => { if (drawn.state !== "unknown") setResult({ state: "none" }); },
    /** Reopening for the next question ends only a confirmed send; a refused one keeps its reason, an unknown one its Check. */
    reset: () => { if (drawn.state === "confirmed") setResult({ state: "none" }); },
  };
}

interface LearnerHelp { refused: boolean; help: MyHelp | undefined; boxes: Box[]; composer: string; reset(): void; ready: boolean }

/**
 * The learner's Help: the thread in its own always-drawn box, and a composer
 * Task that asks one question at a time. A confirmed send rests the composer
 * with "Ask another question", which reopens it empty; the thread stays read.
 */
function useLearnerHelp({ courseId, timezone, arrival, arrivalLabel }: { courseId: string; timezone: string | null; arrival: HelpArrival; arrivalLabel: string | null }): LearnerHelp {
  const { me } = useAuth();
  const client = useQueryClient();
  const field = useId();
  const tenant = me?.tenant_id, user = me?.user_id;
  const { refused, refuse } = useRefusals(`${tenant}:${user}:${courseId}`);
  const lost = refused.has("mine");
  const key = useMemo(() => courseHelpKey(tenant, user, courseId, "mine"), [tenant, user, courseId]);
  const query = useQuery({ queryKey: key, queryFn: refusing("mine", refuse, () => courseHelpApi.mine(courseId)), enabled: Boolean(me) && !lost, retry: false });
  useEffect(() => { if (lost) client.removeQueries({ queryKey: key, exact: true }); }, [lost, client, key]);
  const help = lost ? undefined : query.data;
  const arrived: HelpReference | null = arrival && arrival !== "open"
    ? { ...arrival, label: arrivalLabel || (arrival.kind === "session" ? words.cameFromSession : words.cameFromWork) } : null;
  const draftOf = useTabDrafts<QuestionDraft, PendingSend>("course-help");
  const draft = draftOf({ record: courseId, version: null, initial: { body: "", reference: arrived } });
  const value = draft.value;
  const invalidate = () => void client.invalidateQueries({ queryKey: courseHelpKey(tenant, user, courseId) });
  const send = useSend({ scope: courseId, draft, invalidate, done: words.sent, notSent: words.notSent,
    send: (requestKey, body) => courseHelpApi.ask(courseId, { body: body.trim(),
      session_id: value.reference?.kind === "session" ? value.reference.id : null,
      assignment_id: value.reference?.kind === "work" ? value.reference.id : null }, requestKey),
    check: requestKey => courseHelpApi.check(requestKey, "course-help.ask", [courseId]) });
  const seen = useRef(0);
  // Shown replies are read: the mark only moves forward, so a repeat is harmless.
  const markSeen = useCallback((seq: number) => {
    if (seq <= seen.current) return;
    seen.current = seq;
    void courseHelpApi.markRead(courseId, seq).then(
      () => void client.invalidateQueries({ queryKey: courseHelpKey(tenant, user, courseId, "mine") }),
      () => { seen.current = 0; });
  }, [client, courseId, tenant, user]);
  // What was unread when this Desk first showed the thread stays New here, though showing it saves the read mark.
  const [newAfter, setNewAfter] = useState<number | null>(null);
  useEffect(() => { if (help && newAfter === null) setNewAfter(help.read_through); }, [help, newAfter]);
  const newFrom = (mine: MyHelp) => newAfter ?? mine.read_through;
  const fresh = (mine: MyHelp) => mine.messages.filter(message => message.author.kind === "teacher" && message.seq > newFrom(mine)).length;
  const composer = `course-help-${courseId}`;
  const read: Read<MyHelp> = lost ? { status: "forbidden" } : readOf(query, words.readFailed);
  const messages = help?.messages ?? [];
  const thread: FactBox<MyHelp> | null = messages.length ? {
    id: `course-help-thread-${courseId}`, kind: "fact", label: words.thread, job: words.threadJob, owner: { system: "imam" }, fold: "never", read,
    detail: mine => [words.messages(mine.messages.length), fresh(mine) ? words.newReplies(fresh(mine)) : null].filter(Boolean).join(" · "),
    body: mine => <div className="grid gap-3">
      {mine.read_only && <p role="status" className="text-ui font-medium">{mine.read_only}</p>}
      <HelpMessages messages={mine.messages} timezone={timezone} readThrough={mine.read_through} newAfter={newFrom(mine)} onSeen={markSeen} />
    </div>,
  } : null;
  const deadline = help?.retention_deadline ? words.keptUntil(formatRetentionDate(help.retention_deadline)) : words.keptAfterTerm;
  const waiting = messages.some(message => message.author.kind === "learner" && !message.answered);
  const blockers = (): Blocker[] => [
    ...(!help?.writable ? [{ kind: "missing" as const, part: `help-question-${courseId}`, sentence: help?.read_only ?? words.readFailed }] : []),
    ...(!value.body.trim() ? [{ kind: "missing" as const, part: `help-question-${courseId}`, sentence: words.writeQuestion }] : []),
  ];
  // A kept draft wins over an arrival; a different arrival is offered, never swapped in.
  const offer = arrived && draft.kept && !sameReference(value.reference, arrived) ? arrived : null;
  const state = send.result.state === "unknown" ? { class: "reading" as const, word: words.sendUnknown }
    : send.result.state === "confirmed" ? { class: "done" as const, word: words.sent }
      : draft.kept ? { class: "draft" as const, word: words.question }
        : waiting ? { class: "quiet" as const, word: words.waitingForTeam }
          : { class: "quiet" as const, word: messages.length ? words.askAnother : words.noQuestions };
  const ask: TaskBox<MyHelp> = {
    id: composer, kind: "task", label: words.help, job: words.learnerJob, owner: { system: "imam" },
    read, state: () => state, fold: "choice", draft: draft.kept, result: send.result,
    detail: mine => mine.audience,
    act: { does: "open", verb: messages.length ? words.askAnother : words.askForHelp },
    ...(send.result.state === "confirmed" ? { exits: [{ label: words.askAnother, reopens: true }] as [{ label: string; reopens: true }] } : {}),
    parts: [{
      id: `help-question-${courseId}`, label: words.question, owner: { system: "imam" }, fold: "never", draft: "browser", keep: draft,
      state: value.body ? { class: "draft", word: words.question } : { class: "waiting", word: words.askForHelp },
      render: () => <div className="grid gap-3" data-help-disclosure>
        {help && <div className="grid gap-1 text-ui"><p>{help.audience}</p><p>{help.retention} {deadline}</p></div>}
        {value.reference && <div className="flex flex-wrap items-center gap-2 text-ui">
          <span data-help-reference={value.reference.kind}>{words.aboutReference(value.reference.label)}</span>
          <Button type="button" variant="subtle" size="sm" disabled={send.frozen} onClick={() => { draft.set(was => ({ ...was, reference: null })); send.clear(); }}>{words.wholeCourse}</Button>
        </div>}
        {offer && <div className="text-ui">
          <Button type="button" variant="subtle" size="sm" data-help-offer={offer.kind} disabled={send.frozen} onClick={() => { draft.set(was => ({ ...was, reference: offer })); send.clear(); }}>{words.askAboutInstead(offer.label)}</Button>
        </div>}
        <div className="grid gap-2"><Label htmlFor={`${field}-question`}>{words.questionLabel}</Label>
          <Textarea id={`${field}-question`} data-help-question maxLength={4000} disabled={send.frozen} value={send.frozen ? draft.pending?.body ?? value.body : value.body}
            onChange={event => { draft.set(was => ({ ...was, body: event.target.value })); send.clear(); }} /></div>
      </div>,
    }],
    commit: {
      verb: words.sendQuestion, cap: words.whenYouSend, blockers,
      effects: () => [{ id: "audience", sentence: value.reference ? words.questionAbout(value.reference.label) : words.questionGoes }, { id: "retention", sentence: deadline }],
      note: help?.audience,
      run: requestKey => send.run(requestKey, value.body, blockers()),
    },
  };
  // A closed course with no thread says why Help is closed; one with a thread says it in the thread.
  const closed: FactBox<MyHelp> | null = help && !help.writable && !thread && send.result.state !== "unknown" ? {
    id: composer, kind: "fact", label: words.help, job: words.learnerJob, owner: { system: "imam" }, fold: "never", read,
    detail: mine => mine.read_only ?? "", prose: mine => mine.read_only ?? words.readFailed,
  } : null;
  const asks = !help || help.writable || send.result.state === "unknown" || Boolean(draft.pending);
  const boxes: Box[] = [...(thread ? [thread] : []), ...(asks ? [ask] : closed ? [closed] : [])];
  return { refused: lost, help, boxes, composer, reset: send.reset, ready: Boolean(help?.writable) };
}

/**
 * One teaching thread as read. Its own refusal withdraws the bodies at once;
 * a transient failure keeps the last read visibly stale with a way to read again.
 */
function TeachingThread({ read, refused, timezone }: { read: UseQueryResult<HelpThread> | undefined; refused: boolean; timezone: string | null }) {
  if (refused) return <p role="status" className="text-ui">{words.threadRefused}</p>;
  const again = read && <Button variant="subtle" size="sm" onClick={() => void read.refetch()}>{words.readAgain}</Button>;
  if (!read?.data) return read?.isError ? <p role="status" className="text-ui">{words.threadFailed} {again}</p> : <p className="text-ui text-muted-foreground">{words.readingThread}</p>;
  return <div className="grid gap-3">
    {read.isRefetchError && <p role="status" className="text-ui">{words.threadStale} {again}</p>}
    {!read.data.learner_open && <p className="text-ui">{read.data.can_reply ? words.learnerEnded : words.nothingOutstanding}</p>}
    <HelpMessages messages={read.data.messages} timezone={timezone} />
  </div>;
}

/**
 * The teaching team's Help questions. Each open row reads its thread once, and
 * that read is the only source of a reply: no reply goes without a thread
 * shown, and a reply answers exactly the learner questions that read showed.
 */
function useTeachingHelp({ courseId, timezone, retainedOnly }: { courseId: string; timezone: string | null; retainedOnly: boolean }) {
  const { me } = useAuth();
  const client = useQueryClient();
  const field = useId();
  const tenant = me?.tenant_id, user = me?.user_id;
  const { refused, refuse } = useRefusals(`${tenant}:${user}:${courseId}`);
  const listKey = useMemo(() => courseHelpKey(tenant, user, courseId, "threads"), [tenant, user, courseId]);
  const threadKey = useCallback((threadId: string) => courseHelpKey(tenant, user, courseId, "thread", threadId), [tenant, user, courseId]);
  const listLost = refused.has("threads");
  const query = useQuery({ queryKey: listKey, queryFn: refusing("threads", refuse, () => courseHelpApi.threads(courseId)), enabled: Boolean(me) && !listLost, retry: false });
  const draftOf = useTabDrafts<{ body: string }, PendingSend>("course-help-reply");
  const [toggled, setToggled] = useState<ReadonlyMap<string, boolean>>(() => new Map());
  const [results, setResults] = useState<Record<string, Result>>({});
  const inFlight = useRef(new Map<string, Promise<Result>>());
  const rows = listLost ? [] : query.data?.threads ?? [];
  const startsOpen = (row: HelpThreadRow) => Boolean(draftOf({ record: row.id, version: null, initial: { body: "" } }).pending);
  const reads = useQueries({ queries: rows.map(row => ({
    queryKey: threadKey(row.id),
    queryFn: refusing(`thread:${row.id}`, refuse, () => courseHelpApi.thread(courseId, row.id)),
    enabled: Boolean(me) && (toggled.get(row.id) ?? startsOpen(row)) && !refused.has(`thread:${row.id}`),
    retry: false,
  })) });
  const readOfRow = new Map(rows.map((row, index) => [row.id, reads[index]]));
  useEffect(() => {
    if (refused.has("threads")) client.removeQueries({ queryKey: listKey, exact: true });
    for (const read of refused) if (read.startsWith("thread:")) client.removeQueries({ queryKey: threadKey(read.slice(7)), exact: true });
    // A thread refused while its list still shows it: the list reads again under its own authority.
    if ([...refused].some(read => read.startsWith("thread:")) && !refused.has("threads")) void client.invalidateQueries({ queryKey: listKey, exact: true });
  }, [refused, client, listKey, threadKey]);
  const invalidate = () => void client.invalidateQueries({ queryKey: courseHelpKey(tenant, user, courseId) });
  // A teacher outside the active assigned team is refused without disclosure: no box.
  if (listLost) return null;
  // Under a Desk refusal, only a successfully read list with threads is drawn (H-1).
  if (retainedOnly && !(query.status === "success" && rows.length)) return null;
  const exclusive = (scope: string, run: () => Promise<Result>) => {
    const running = inFlight.current.get(scope);
    if (running) return running;
    const request = Promise.resolve().then(run).finally(() => { inFlight.current.delete(scope); });
    inFlight.current.set(scope, request);
    return request;
  };
  const task = (row: HelpThreadRow): RowTask | undefined => {
    // A refused thread offers no reply at all.
    if (refused.has(`thread:${row.id}`)) return undefined;
    const read = readOfRow.get(row.id);
    // Only a thread read that succeeded, and is what the detail shows, supports a reply; a stale or failed one blocks it.
    const shown = read?.status === "success" ? read.data : undefined;
    const draft = draftOf({ record: row.id, version: null, initial: { body: "" } });
    const setResult = (next: Result) => setResults(was => ({ ...was, [row.id]: next }));
    const unknown = (pending: PendingSend): Result => ({ state: "unknown", cap: words.notSent, requestId: pending.key, sentence: words.sendUnknown,
      check: { verb: words.checkStatus, note: words.checkOnly, run: () => exclusive(row.id, async () => {
        try {
          const answer = await courseHelpApi.check(pending.key, "course-help.reply", [courseId, row.id]);
          if (answer.state === "not_committed") return settle({ state: "refused", sentence: words.notSentCheck });
          return settle("receipt" in answer && answer.receipt.result.state === "confirmed" ? { state: "confirmed", sent: answer.receipt.result } : { state: "unknown" }, pending);
        } catch { return settle({ state: "unknown" }, pending); }
      }) } });
    function settle(decision: Decision, pending?: PendingSend): Result {
      const next: Result = decision.state === "unknown" && pending ? unknown(pending)
        : decision.state === "confirmed" ? { state: "confirmed", at: new Date().toISOString(), effects: [words.replySent], remains: [] }
          : { state: "refused", cap: words.notSent, sentence: decision.state === "refused" ? decision.sentence : words.sendUnknown, draft: "kept" };
      if (next.state !== "unknown") draft.setPending(null);
      if (decision.state === "confirmed") { draft.discard(); invalidate(); }
      setResult(next);
      return next;
    }
    const result = results[row.id] ?? { state: "none" };
    const drawn: Result = result.state === "none" && draft.pending && !inFlight.current.has(row.id) ? unknown(draft.pending) : result;
    const part = `help-reply-${row.id}`;
    const blockers = (): Blocker[] => [
      ...(!shown ? [{ kind: "missing" as const, part, sentence: read?.isError ? words.threadStale : words.readBeforeReply }] : []),
      ...(shown && !shown.can_reply ? [{ kind: "missing" as const, part, sentence: words.nothingOutstanding }] : []),
      ...(!draft.value.body.trim() ? [{ kind: "missing" as const, part, sentence: words.writeReply }] : []),
    ];
    const frozen = Boolean(draft.pending);
    const unanswered = shown?.unanswered ?? row.unanswered;
    return {
      parts: [{ id: part, label: words.reply, owner: { system: "imam" }, fold: "never", draft: "browser", keep: draft,
        state: draft.value.body ? { class: "draft", word: words.reply } : { class: unanswered ? "waiting" : "quiet", word: unanswered ? words.unanswered(unanswered) : words.answered },
        render: () => shown && !shown.can_reply ? <p className="text-ui">{words.nothingOutstanding}</p> : <div className="grid gap-2"><Label htmlFor={`${field}-${row.id}`}>{words.replyLabel}</Label>
          <Textarea id={`${field}-${row.id}`} data-help-reply maxLength={4000} disabled={frozen} value={frozen ? draft.pending?.body ?? draft.value.body : draft.value.body}
            onChange={event => { draft.set({ body: event.target.value }); if (drawn.state !== "unknown") setResult({ state: "none" }); }} /></div> }],
      commit: { verb: words.sendReply, cap: words.whenYouSend, blockers, note: words.replyNamed,
        effects: () => [{ id: "reply", sentence: words.replyGoes(shown?.learner_name ?? row.learner_name, unanswered) }],
        run: key => exclusive(row.id, async () => {
          if (draft.pending) return unknown(draft.pending);
          const blocked = blockers()[0];
          if (!shown || blocked && "sentence" in blocked) return settle({ state: "refused", sentence: blocked && "sentence" in blocked ? blocked.sentence : words.readBeforeReply });
          const pending = { key, body: draft.value.body };
          draft.setPending(pending);
          // The cutoff is the shown read's: a question arriving after it stays unanswered.
          try { return settle({ state: "confirmed", sent: await courseHelpApi.reply(courseId, row.id, { body: pending.body.trim(), answers_through: shown.last_learner_seq }, key) }, pending); }
          catch (error) {
            return settle(error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 408
              ? { state: "refused", sentence: reasonOnError(error)?.message ?? error.message } : { state: "unknown" }, pending);
          }
        }) },
      result: drawn,
    };
  };
  const count = query.data?.unanswered_threads ?? 0;
  const box: ListBox<HelpThreads, HelpThreadRow> = {
    id: `course-help-threads-${courseId}`, kind: "list", label: words.helpQuestions, job: words.teachingJob, owner: { system: "imam" }, fold: "never",
    read: readOf(query, words.readFailed, value => value.threads.length ? null : words.noLearnerAsked),
    state: () => count ? { class: "waiting", word: words.unanswered(count) } : { class: "done", word: words.allAnswered },
    detail: () => count ? words.unanswered(count) : words.allAnswered,
    rows: value => value.threads,
    row: {
      key: row => row.id, title: row => row.learner_name,
      state: row => row.unanswered ? { class: "waiting", word: words.unanswered(row.unanswered) } : { class: "done", word: words.answered },
      facts: row => [row.last_at ? formatCourseInstant(row.last_at, timezone) : null, row.learner_open ? null : words.learnerEnded].filter(Boolean).join(" · "),
      act: row => ({ does: "open", verb: row.unanswered ? words.replyVerb : words.readThreadVerb }),
      detail: row => <TeachingThread read={readOfRow.get(row.id)} refused={refused.has(`thread:${row.id}`)} timezone={timezone} />,
      task,
      startsOpen,
    },
  };
  return { box, onRowOpenChange: (_box: string, key: string, open: boolean) => setToggled(was => new Map(was).set(key, open)) };
}

/**
 * Help on the Desk: the learner's thread, or the teaching team's questions.
 * Placed after Your work (DESK-FIRST contract 5). `retainedOnly` is Help under
 * a Desk refusal: a learner's existing thread, or a teaching list that reads,
 * and nothing else of the course.
 */
export function CourseHelp({ courseId, teaching, timezone, retainedOnly = false }: { courseId: string; teaching: boolean; timezone: string | null; retainedOnly?: boolean }) {
  return teaching ? <TeachingHelp courseId={courseId} timezone={timezone} retainedOnly={retainedOnly} /> : <LearnerHelp courseId={courseId} timezone={timezone} retainedOnly={retainedOnly} />;
}

function LearnerHelp({ courseId, timezone, retainedOnly }: { courseId: string; timezone: string | null; retainedOnly: boolean }) {
  const [params] = useSearchParams();
  const location = useLocation();
  const arrival = readHelpArrival(params);
  const label = (location.state as { helpLabel?: string | null } | null)?.helpLabel ?? null;
  const learner = useLearnerHelp({ courseId, timezone, arrival, arrivalLabel: label });
  const section = useRef<HTMLElement>(null);
  const focused = useRef(false);
  const pending = learner.boxes.some(box => box.kind === "task" && box.result.state === "unknown");
  // A valid arrival lands on the question once it can be written; a refused or closed Help leaves the Desk's focus alone.
  useEffect(() => {
    if (!arrival || focused.current || !learner.ready) return;
    const question = section.current?.querySelector<HTMLElement>("[data-help-question]");
    if (!question) return;
    focused.current = true;
    question.scrollIntoView?.({ block: "center" });
    question.focus({ preventScroll: true });
  }, [arrival, learner.ready]);
  if (learner.refused) return retainedOnly ? null : <section aria-label={words.help} data-course-help="learning"><p role="status" className="text-ui">{words.refused}</p></section>;
  if (retainedOnly && !learner.help?.thread_id) return null;
  return <section ref={section} aria-label={words.help} data-course-help="learning">
    <Bento id={`course-help-${courseId}`} journey="course-help" boxes={learner.boxes} defaultOpen={arrival || pending ? [learner.composer] : []}
      onOpenChange={(id, open) => { if (open && id === learner.composer) learner.reset(); }} />
  </section>;
}

function TeachingHelp({ courseId, timezone, retainedOnly }: { courseId: string; timezone: string | null; retainedOnly: boolean }) {
  const teaching = useTeachingHelp({ courseId, timezone, retainedOnly });
  if (!teaching) return null;
  return <section aria-label={words.helpQuestions} data-course-help="teaching">
    <Bento id={`course-help-threads-${courseId}`} journey="course-help" boxes={[teaching.box]} onRowOpenChange={teaching.onRowOpenChange} />
  </section>;
}
